import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MatKeyboardRef } from '../classes';
import { KeyboardComponent } from '../components';
import { KeyboardService } from '../services/keyboard.service';

export const KEYBOARD_STORAGE_KEY = 'qt_KEYBOARD_INPUT';

@Directive({
  selector: 'input[qtKeyboard], textarea[qtKeyboard]',
  standalone: false,
})
export class KeyboardDirective implements OnDestroy {
  elementRef = inject(ElementRef);
  keyboardService = inject(KeyboardService);
  control? = inject(NgControl, { optional: true, self: true });

  public keyboardRef: MatKeyboardRef<KeyboardComponent>;
  public subscriptions: Subscription = new Subscription();
  public type: string;
  private shouldRestoreType = false;

  @Input() qtKeyboard = 'US Standard';
  @Input() duration: number;
  @Input() isDebug: boolean;

  @Output() enterClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() capsClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() altClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() shiftClick: EventEmitter<void> = new EventEmitter<void>();

  @HostListener('focus')
  public showKeyboard(): void {
    if (!localStorage.getItem(KEYBOARD_STORAGE_KEY)) {
      return;
    }
    if (this.elementRef?.nativeElement) {
      this.type = this.elementRef.nativeElement.type;
      this.shouldRestoreType = this.type !== 'number';
      if (this.shouldRestoreType) {
        (this.elementRef.nativeElement as HTMLInputElement).setAttribute(
          'type',
          'text'
        );
      }
    }

    this.keyboardRef = this.keyboardService.open(this.qtKeyboard, {
      duration: this.duration,
      isDebug: this.isDebug,
    });

    this.keyboardRef.instance.setInputInstance(this.elementRef);

    if (this.control) {
      this.keyboardRef.instance.attachControl(this.control.control);
    }

    this.subscriptions.add(
      this.keyboardRef.instance.enterClick.subscribe(() =>
        this.enterClick.next()
      )
    );
    this.subscriptions.add(
      this.keyboardRef.instance.capsClick.subscribe(() => this.capsClick.next())
    );
    this.subscriptions.add(
      this.keyboardRef.instance.altClick.subscribe(() => this.altClick.next())
    );
    this.subscriptions.add(
      this.keyboardRef.instance.shiftClick.subscribe(() =>
        this.shiftClick.next()
      )
    );
  }

  @HostListener('blur')
  public hideKeyboard(): void {
    if (this.elementRef?.nativeElement) {
      if (this.shouldRestoreType) {
        (this.elementRef.nativeElement as HTMLInputElement).setAttribute(
          'type',
          this.type
        );
      }
      this.shouldRestoreType = false;
    }

    if (this.keyboardRef) {
      this.keyboardRef.dismiss();
    }
    this.subscriptions.unsubscribe();
  }

  ngOnDestroy() {
    this.hideKeyboard();
  }
}
